import {
  ApiEpBilling,
  FailureApi,
  IApiEpBillingPaymentProvider,
  IApiEpBillingProduct,
  IApiEpBillingProductPurchaseRes,
} from "@smp/api";
import { REACT_APP__BASE_PATH } from "@smp/utils";
import { useSnackbar } from "notistack";
import { useMutation, UseMutationOptions, useQueryClient } from "react-query";

import { useFailureTranslator } from "@/hooks/use_failure_translator";
import { IContextApp } from "@/screens/app/context/app";

type Params = {
  product: IApiEpBillingProduct;
  paymentType: IContextApp["state"]["billingPaymentType"];
  paymentProvider: IApiEpBillingPaymentProvider;
  isInternalAccountUse: boolean;
};

export const useMutationBillingProductBill = (
  isPaymentSuccessAutoClose?: boolean,
  options?: UseMutationOptions<
    IApiEpBillingProductPurchaseRes,
    FailureApi,
    Params
  >,
) => {
  const { enqueueSnackbar } = useSnackbar();
  const { failureTranslationGet } = useFailureTranslator();
  const queryClient = useQueryClient();

  return useMutation<IApiEpBillingProductPurchaseRes, FailureApi, Params>(
    ({ product, isInternalAccountUse, paymentProvider, paymentType }) => {
      const fn = (() => {
        if (product.type === "feature-subscription") {
          return ApiEpBilling.Product.FeatureSubscription.purchase;
        } else if (product.type === "plan-product") {
          return ApiEpBilling.Product.PlanProduct.purchase;
        } else if (product.type === "token") {
          return ApiEpBilling.Product.TokenPackage.purchase;
        } else {
          return ApiEpBilling.Product.PostsPackage.purchase;
        }
      })();

      const urlOrigin =
        window.location.host === "localhost"
          ? "https://staging.smmplanner.com"
          : window.location.origin;

      return fn({
        body: {
          allow_internal_funds: isInternalAccountUse,
          payment_provider: paymentProvider.id,
          payment_type: paymentType,
          payment_url_cancel:
            urlOrigin + REACT_APP__BASE_PATH + "/public/purchase/cancel",
          payment_url_fail:
            urlOrigin + REACT_APP__BASE_PATH + "/public/purchase/fail",
          payment_url_success:
            urlOrigin +
            REACT_APP__BASE_PATH +
            "/public/purchase/success" +
            (isPaymentSuccessAutoClose ? "?auto_close=1" : ""),
        },
        path: { id: product.id },
      });
    },
    {
      ...options,
      onSuccess: (...args) => {
        queryClient.invalidateQueries("infinite__ep.billing.bill", {
          refetchInactive: true,
        });

        options?.onSuccess?.(...args);
      },
      onError: (...args) => {
        enqueueSnackbar(failureTranslationGet(args[0]), {
          variant: "error",
        });

        options?.onError?.(...args);
      },
    },
  );
};
